import React, { useMemo, useState } from "react";
import {
  ComposedChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const COLORS = {
  duration: "#2a78d6",
  quiz: "#2a78d6",
  example: "#eb6834",
  practice: "#1baf7a",
  assignment: "#eda100",
  notesCompleted: "#e87ba4",
};

const GRID = "#e1e0d9";
const AXIS_LINE = "#c3c2b7";
const MUTED = "#898781";
const SURFACE = "#fcfcfb";

const QUESTION_LABELS = {
  quiz: "Quiz",
  example: "Example",
  practice: "Practice",
  assignment: "Assignment",
};

const formatDateTick = (dateIST) => {
  const [, m, d] = dateIST.split("-");
  return `${d}/${m}`;
};

// Splits duration into two parallel series (approximate / real) so the
// two regions can carry different stroke styles on the same hue, with the
// boundary point duplicated into both so the lines meet with no visual gap.
function buildDurationSeries(data) {
  const minutes = data.map((d) => Math.round((d.durationSeconds / 60) * 10) / 10);
  const real = data.map((d, i) => (d.durationIsApproximate ? null : minutes[i]));
  const approx = data.map((d, i) => (d.durationIsApproximate ? minutes[i] : null));

  for (let i = 1; i < data.length; i++) {
    const wasApprox = data[i - 1].durationIsApproximate;
    const isApprox = data[i].durationIsApproximate;
    if (wasApprox !== isApprox) {
      if (wasApprox && !isApprox) {
        approx[i] = minutes[i];
        real[i - 1] = minutes[i - 1];
      } else {
        real[i] = minutes[i];
        approx[i - 1] = minutes[i - 1];
      }
    }
  }
  return { real, approx };
}

const DurationTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload.find((p) => p.value !== null && p.value !== undefined);
  if (!point) return null;
  const isApprox = point.dataKey === "durationApprox";
  return (
    <div className="dash-tooltip">
      <div className="dash-tooltip-label">{label}</div>
      <div className="dash-tooltip-row">
        <span className="dash-tooltip-key" style={{ background: COLORS.duration }} />
        <span className="dash-tooltip-value">{point.value} min</span>
        <span className="dash-tooltip-name">{isApprox ? "approximate" : "tracked"}</span>
      </div>
    </div>
  );
};

const QuestionsTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="dash-tooltip">
      <div className="dash-tooltip-label">{label}</div>
      {payload.map((p) => (
        <div className="dash-tooltip-row" key={p.dataKey}>
          <span className="dash-tooltip-key" style={{ background: p.color }} />
          <span className="dash-tooltip-value">{p.value}</span>
          <span className="dash-tooltip-name">
            {p.dataKey === "notesCompleted" ? "Notes completed" : QUESTION_LABELS[p.dataKey]}
          </span>
        </div>
      ))}
    </div>
  );
};

const ActivityChart = ({ data, loading, error }) => {
  const [showTable, setShowTable] = useState(false);

  const chartData = useMemo(() => {
    if (!data || !data.length) return [];
    const { real, approx } = buildDurationSeries(data);
    return data.map((d, i) => ({
      date: formatDateTick(d.dateIST),
      dateIST: d.dateIST,
      durationReal: real[i],
      durationApprox: approx[i],
      durationMinutes: Math.round((d.durationSeconds / 60) * 10) / 10,
      durationIsApproximate: d.durationIsApproximate,
      notesCompleted: d.notesCompleted,
      quiz: d.questionsSubmitted?.quiz || 0,
      example: d.questionsSubmitted?.example || 0,
      practice: d.questionsSubmitted?.practice || 0,
      assignment: d.questionsSubmitted?.assignment || 0,
    }));
  }, [data]);

  const hasAnyApprox = chartData.some((d) => d.durationIsApproximate);

  if (error) {
    return <div className="dash-section-error">Couldn't load activity data: {error}</div>;
  }

  if (loading) {
    return <div className="dash-chart-skeleton" />;
  }

  if (!chartData.length) {
    return <div className="dash-section-empty">No activity in this date range.</div>;
  }

  return (
    <div className="dash-activity-charts">
      <div className="dash-chart-panel">
        <div className="dash-chart-panel-header">
          <h3 className="dash-chart-panel-title">App usage duration</h3>
          {hasAnyApprox && (
            <div className="dash-chart-caption">
              <span className="dash-caption-swatch dash-caption-solid" /> tracked
              <span className="dash-caption-swatch dash-caption-dashed" /> approximate (from activity logs, before tracking shipped)
            </div>
          )}
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="date" tick={{ fill: MUTED, fontSize: 11 }} axisLine={{ stroke: AXIS_LINE }} tickLine={false} />
            <YAxis
              tick={{ fill: MUTED, fontSize: 11 }}
              axisLine={{ stroke: AXIS_LINE }}
              tickLine={false}
              width={40}
              label={{ value: "minutes", angle: -90, position: "insideLeft", fill: MUTED, fontSize: 11 }}
            />
            <Tooltip content={<DurationTooltip />} />
            <Area
              type="monotone"
              dataKey="durationReal"
              stroke={COLORS.duration}
              strokeWidth={2}
              fill={COLORS.duration}
              fillOpacity={0.1}
              connectNulls={false}
              dot={{ r: 4, fill: COLORS.duration, stroke: SURFACE, strokeWidth: 2 }}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="durationApprox"
              stroke={COLORS.duration}
              strokeWidth={2}
              strokeDasharray="4 3"
              fill={COLORS.duration}
              fillOpacity={0.04}
              connectNulls={false}
              dot={{ r: 4, fill: SURFACE, stroke: COLORS.duration, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="dash-chart-panel">
        <div className="dash-chart-panel-header">
          <h3 className="dash-chart-panel-title">Questions submitted &amp; notes completed</h3>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="date" tick={{ fill: MUTED, fontSize: 11 }} axisLine={{ stroke: AXIS_LINE }} tickLine={false} />
            <YAxis
              tick={{ fill: MUTED, fontSize: 11 }}
              axisLine={{ stroke: AXIS_LINE }}
              tickLine={false}
              width={36}
              allowDecimals={false}
            />
            <Tooltip content={<QuestionsTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: 12, color: "#52514e" }}
              formatter={(value) => (value === "notesCompleted" ? "Notes completed" : QUESTION_LABELS[value] || value)}
            />
            <Bar dataKey="quiz" stackId="q" fill={COLORS.quiz} stroke={SURFACE} strokeWidth={2} />
            <Bar dataKey="example" stackId="q" fill={COLORS.example} stroke={SURFACE} strokeWidth={2} />
            <Bar dataKey="practice" stackId="q" fill={COLORS.practice} stroke={SURFACE} strokeWidth={2} />
            <Bar dataKey="assignment" stackId="q" fill={COLORS.assignment} stroke={SURFACE} strokeWidth={2} radius={[4, 4, 0, 0]} />
            <Line
              type="monotone"
              dataKey="notesCompleted"
              stroke={COLORS.notesCompleted}
              strokeWidth={2}
              dot={{ r: 4, fill: COLORS.notesCompleted, stroke: SURFACE, strokeWidth: 2 }}
              activeDot={{ r: 5, stroke: SURFACE, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <button type="button" className="dash-table-toggle" onClick={() => setShowTable((v) => !v)}>
        {showTable ? "Hide data table" : "View as table"}
      </button>

      {showTable && (
        <div className="dash-table-wrap">
          <table className="dash-data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Duration (min)</th>
                <th>Quiz</th>
                <th>Example</th>
                <th>Practice</th>
                <th>Assignment</th>
                <th>Notes completed</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((d) => (
                <tr key={d.dateIST}>
                  <td>{d.dateIST}</td>
                  <td>
                    {d.durationMinutes}
                    {d.durationIsApproximate ? " (approx.)" : ""}
                  </td>
                  <td>{d.quiz}</td>
                  <td>{d.example}</td>
                  <td>{d.practice}</td>
                  <td>{d.assignment}</td>
                  <td>{d.notesCompleted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActivityChart;
